import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, ShoppingCart, User, Menu, X, Phone, Mail, MapPin, Facebook, MessageCircle, BarChart3, Youtube, Instagram, Truck, Home as HomeIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider, useCart } from './context/CartContext';
import { setConfirmHandler } from './lib/ui-feedback';
import Home from './pages/Home';

const AuthModal = React.lazy(() => import('./components/AuthModal'));
const LiveSearch = React.lazy(() =>
  import('./components/LiveSearch').then((module) => ({
    default: module.LiveSearch,
  })),
);

const ProductDetails = React.lazy(() => import('./pages/ProductDetails'));
const Shop = React.lazy(() => import('./pages/Shop'));
const CartPage = React.lazy(() => import('./pages/Cart'));
const Checkout = React.lazy(() => import('./pages/Checkout'));
const Profile = React.lazy(() => import('./pages/Profile'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const PageViewer = React.lazy(() => import('./pages/PageViewer'));
const TrackOrder = React.lazy(() => import('./pages/TrackOrder'));

type AppToast = {
  id: number;
  message: string;
};

type ConfirmState = {
  open: boolean;
  message: string;
  resolve: ((value: boolean) => void) | null;
};

const PremiumNotifications = ({
  toasts,
  removeToast,
}: {
  toasts: AppToast[];
  removeToast: (id: number) => void;
}) => (
  <div className="fixed top-4 right-4 z-[120] space-y-3 w-[calc(100vw-2rem)] max-w-sm pointer-events-none">
    <AnimatePresence>
      {toasts.map((toast) => (
        <motion.div
          key={toast.id}
          initial={{ opacity: 0, y: -10, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.98 }}
          transition={{ duration: 0.2 }}
          className="pointer-events-auto rounded-2xl border border-slate-200 bg-white/95 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.15)] p-4"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-emerald-600">
                Notification
              </p>
              <p className="text-sm text-slate-700 mt-1 leading-relaxed break-words">
                {toast.message}
              </p>
            </div>
            <button
              onClick={() => removeToast(toast.id)}
              className="p-1 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
              aria-label="Dismiss notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      ))}
    </AnimatePresence>
  </div>
);


const CartDrawer = () => {
  const { isOpen, toggleCart, items, subtotal, removeFromCart } = useCart();
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            onClick={toggleCart}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[60]"
          />
          <motion.div 
            initial={{ x: '100%' }} 
            animate={{ x: 0 }} 
            exit={{ x: '100%' }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-[70] flex flex-col"
          >
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-xl font-bold">Shopping Cart</h2>
              <button onClick={toggleCart} className="p-2 hover:bg-slate-100 rounded-full" aria-label="Close cart"><X className="w-6 h-6" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-400 space-y-4">
                  <ShoppingCart className="w-12 h-12 opacity-20" />
                  <p>Your cart is empty</p>
                </div>
              ) : (
                items.map(item => (
                  <div key={item.cartItemId} className="flex gap-4">
                    <img src={item.image} alt={item.name} className="w-20 h-20 bg-slate-100 rounded-xl object-cover" />
                    <div className="flex-1">
                      <h4 className="font-medium text-sm leading-tight">{item.name}</h4>
                      {item.variants && Object.keys(item.variants).length > 0 && (
                        <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider font-bold">
                          {Object.entries(item.variants).map(([k, v]) => `${k}: ${v}`).join(', ')}
                        </p>
                      )}
                      <p className="text-slate-500 text-sm mt-1">Qty: {item.quantity}</p>
                      <p className="font-semibold mt-1 text-sm">৳{item.price}</p>
                    </div>
                    <button onClick={() => removeFromCart(item.cartItemId)} className="text-slate-400 hover:text-red-500 self-start" aria-label={`Remove ${item.name} from cart`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>

            {items.length > 0 && (
              <div className="p-6 border-t border-slate-100 bg-slate-50/50">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-slate-500 font-semibold text-xs uppercase tracking-widest">Subtotal</span>
                  <span className="text-2xl font-bold font-mono tracking-tighter text-blue-600">৳{subtotal.toFixed(2)}</span>
                </div>
                <button 
                  onClick={() => { toggleCart(); navigate('/checkout'); }}
                  className="w-full bg-slate-900 text-white py-4 rounded-full font-bold hover:bg-blue-600 transition-all shadow-lg"
                >
                  Checkout Now
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default function App() {
  const [isAuthOpen, setIsAuthOpen] = React.useState(false);
  // We need useCart here to make the mobile nav work
  // But useCart must be used inside CartProvider
  // So we move the layout into a separate component
  return (
    <Router>
      <AuthProvider>
        <CartProvider>
          <AppContent isAuthOpen={isAuthOpen} setIsAuthOpen={setIsAuthOpen} />
        </CartProvider>
      </AuthProvider>
    </Router>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

const SearchFallback = () => (
  <div className="hidden md:flex flex-1 max-w-md mx-8 h-10 rounded-full bg-slate-100" />
);

function AppContent({ isAuthOpen, setIsAuthOpen }: any) {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [toasts, setToasts] = React.useState<AppToast[]>([]);
  const [confirmState, setConfirmState] = React.useState<ConfirmState>({
    open: false,
    message: "",
    resolve: null,
  });
  const { user, isAdmin } = useAuth();
  const { toggleCart, items } = useCart();
  const location = useLocation();
  const isAdminPath = location.pathname.startsWith('/admin');
  const [settings, setSettings] = React.useState<any>(null);
  const resolvedLogoImage =
    settings?.logo_image ||
    (typeof settings?.logo === "string" &&
    settings.logo.startsWith("data:image/")
      ? settings.logo
      : "");
  const cartCount = items.reduce((acc, item) => acc + item.quantity, 0);

  React.useEffect(() => {
    const cached = localStorage.getItem('eg_site_settings_cache');
    if (cached) {
      try {
        setSettings(JSON.parse(cached));
      } catch {}
    }

    const fetchSettings = async () => {
      const { supabase } = await import('./lib/supabase');
      const { data } = await supabase.from('site_settings').select('*').eq('id', 'primary').single();
      if (data) {
        setSettings(data);
        localStorage.setItem('eg_site_settings_cache', JSON.stringify(data));
        if (data.favicon) {
          const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
          if (link) {
            link.href = data.favicon;
          } else {
            const newLink = document.createElement('link');
            newLink.rel = 'icon';
            newLink.href = data.favicon;
            document.head.appendChild(newLink);
          }
        }
      }
    };
    fetchSettings();
  }, []);

  React.useEffect(() => {
    if (user && isAuthOpen) {
      setIsAuthOpen(false);
    }
  }, [user, isAuthOpen, setIsAuthOpen]);

  React.useEffect(() => {
    const originalAlert = window.alert;
    const originalConfirm = window.confirm;
    window.alert = (message?: any) => {
      const text = String(message ?? "");
      const id = Date.now() + Math.floor(Math.random() * 1000);
      setToasts((prev) => [...prev, { id, message: text }]);
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 4500);
    };
    window.confirm = (message?: string) => {
      const text = String(message ?? "");
      const id = Date.now() + Math.floor(Math.random() * 1000);
      setToasts((prev) => [
        ...prev,
        { id, message: `Please use in-app action for: ${text}` },
      ]);
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 3000);
      return false;
    };
    return () => {
      window.alert = originalAlert;
      window.confirm = originalConfirm;
    };
  }, []);

  React.useEffect(() => {
    setConfirmHandler((message: string) =>
      new Promise<boolean>((resolve) => {
        setConfirmState({ open: true, message, resolve });
      }),
    );
    return () => setConfirmHandler(null);
  }, []);

  const removeToast = React.useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);
  
  return (
    <div className="min-h-screen flex flex-col relative">
      <PremiumNotifications toasts={toasts} removeToast={removeToast} />
      <AnimatePresence>
        {confirmState.open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[110]"
            />
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[111] w-[calc(100vw-2rem)] max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.25)]"
            >
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400 mb-2">
                Please Confirm
              </p>
              <p className="text-slate-800 text-base leading-relaxed">
                {confirmState.message}
              </p>
              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    confirmState.resolve?.(false);
                    setConfirmState({ open: false, message: "", resolve: null });
                  }}
                  className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    confirmState.resolve?.(true);
                    setConfirmState({ open: false, message: "", resolve: null });
                  }}
                  className="px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold hover:bg-black transition-colors"
                >
                  Confirm
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <Helmet 
        titleTemplate={`%s | ${settings?.seo_title || settings?.logo || ''}`} 
        defaultTitle={settings?.seo_title || settings?.logo || ''}
      >
        {(settings?.seo_description || settings?.footer_text) && (
          <meta name="description" content={settings?.seo_description || settings?.footer_text} />
        )}
        {settings?.seo_keywords && <meta name="keywords" content={settings?.seo_keywords} />}
        
        {settings?.og_title && <meta property="og:title" content={settings.og_title} />}
        {settings?.og_description && <meta property="og:description" content={settings.og_description} />}
        {settings?.og_image && <meta property="og:image" content={settings.og_image} />}
      </Helmet>
      <ScrollToTop />
      {!isAdminPath && (
        <header className="nav-blur px-4 py-3 md:px-8 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4 shrink-0">
              <button 
                onClick={() => setIsMenuOpen(true)}
                className="hidden md:flex p-2 hover:bg-slate-100 rounded-full text-slate-700 transition-colors"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6" />
              </button>
              <Link to="/" className="text-xl md:text-2xl font-black tracking-tighter text-slate-900 uppercase flex items-center gap-2 min-w-[140px] md:min-w-[180px]">
                {resolvedLogoImage ? (
                  <img src={resolvedLogoImage} alt="Logo" className="h-6 md:h-8 w-[120px] md:w-[180px] object-contain" width={180} height={32} decoding="async" />
                ) : (
                  <>{settings?.logo || ''}</>
                )}
              </Link>
            </div>

            {/* Live Search */}
            <React.Suspense fallback={<SearchFallback />}>
              <LiveSearch />
            </React.Suspense>

            <div className="flex items-center gap-2 md:gap-4">
              <div className="hidden md:flex w-[108px] justify-center">
                {isAdmin ? (
                  <Link to="/admin" className="flex items-center gap-1 px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-blue-100 transition-colors">
                    <BarChart3 className="w-3 h-3" /> Admin
                  </Link>
                ) : null}
              </div>

              <Link to="/track" className="hidden md:flex items-center gap-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-slate-200 transition-colors">
                Track Order
              </Link>

              {user ? (
                <Link to="/profile" className="p-2 hover:bg-slate-100 rounded-full relative" aria-label="Open profile">
                  <User className="w-5 h-5 text-slate-700" />
                  <div className="absolute top-2 right-2 w-2 h-2 bg-blue-500 rounded-full border-2 border-white" />
                </Link>
              ) : (
                <button onClick={() => setIsAuthOpen(true)} className="p-2 hover:bg-slate-100 rounded-full relative" aria-label="Sign in">
                  <User className="w-5 h-5 text-slate-700" />
                </button>
              )}
              
              <button onClick={toggleCart} className="p-2 hover:bg-slate-100 rounded-full relative" aria-label="Open cart">
                <ShoppingCart className="w-5 h-5 text-slate-700" />
              </button>
            </div>
          </div>
        </header>
      )}

      <CartDrawer />
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[60] md:flex hidden"
            />
            <motion.div 
              initial={{ x: '-100%' }} 
              animate={{ x: 0 }} 
              exit={{ x: '-100%' }}
              className="fixed left-0 top-0 h-full w-full max-w-[300px] bg-white shadow-2xl z-[70] hidden md:flex flex-col"
            >
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <Link to="/" onClick={() => setIsMenuOpen(false)} className="text-xl font-black tracking-tighter text-slate-900 uppercase flex items-center gap-2">
                  {resolvedLogoImage ? (
                    <img src={resolvedLogoImage} alt="Logo" className="h-6 w-[120px] object-contain" width={120} height={24} decoding="async" />
                  ) : (
                    <>{settings?.logo || ''}</>
                  )}
                </Link>
                <button onClick={() => setIsMenuOpen(false)} className="p-2 hover:bg-slate-100 rounded-full" aria-label="Close menu"><X className="w-5 h-5" /></button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-2">
                <Link to="/" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-blue-50 hover:text-blue-600 font-bold transition-colors text-slate-700">
                  <HomeIcon className="w-5 h-5" /> Home
                </Link>
                <Link to="/shop" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-blue-50 hover:text-blue-600 font-bold transition-colors text-slate-700">
                  <Search className="w-5 h-5" /> Shop
                </Link>
                <Link to="/track" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-blue-50 hover:text-blue-600 font-bold transition-colors text-slate-700">
                  <Truck className="w-5 h-5" /> Track Order
                </Link>
                <Link to="/profile" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-blue-50 hover:text-blue-600 font-bold transition-colors text-slate-700">
                  <User className="w-5 h-5" /> Profile
                </Link>
                {isAdmin && (
                  <Link to="/admin" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-blue-50 hover:text-blue-600 font-bold transition-colors text-slate-700">
                    <BarChart3 className="w-5 h-5" /> Admin Dashboard
                  </Link>
                )}
              </div>
              <div className="p-6 border-t border-slate-100">
                <div className="flex gap-4 mb-4">
                  {settings?.facebook_link && (
                    <a href={settings.facebook_link} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="p-2 bg-blue-50 rounded-full hover:bg-blue-100 transition-colors">
                      <Facebook className="w-4 h-4 text-blue-600" />
                    </a>
                  )}
                  {settings?.youtube_link && (
                    <a href={settings.youtube_link} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="p-2 bg-red-50 rounded-full hover:bg-red-100 transition-colors">
                      <Youtube className="w-4 h-4 text-red-600" />
                    </a>
                  )}
                  {settings?.instagram_link && (
                    <a href={settings.instagram_link} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="p-2 bg-pink-50 rounded-full hover:bg-pink-100 transition-colors">
                      <Instagram className="w-4 h-4 text-pink-600" />
                    </a>
                  )}
                </div>
                {settings?.phone && (
                  <p className="text-xs font-medium text-slate-500 flex items-center gap-2"><Phone className="w-3 h-3" /> {settings.phone}</p>
                )}
                {settings?.contact_email && (
                  <p className="text-xs font-medium text-slate-500 mt-2 flex items-center gap-2"><Mail className="w-3 h-3" /> {settings.contact_email}</p>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {isAuthOpen && (
        <React.Suspense fallback={null}>
          <AuthModal isOpen={isAuthOpen} onClose={() => setIsAuthOpen(false)} />
        </React.Suspense>
      )}
      <main className="flex-1">
        <React.Suspense fallback={<div className="p-8 text-center text-slate-500">Loading...</div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/product/:slug" element={<ProductDetails />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/track" element={<TrackOrder />} />
            <Route path="/admin/*" element={<AdminDashboard />} />
            <Route path="/about" element={<PageViewer />} />
            <Route path="/privacy" element={<PageViewer />} />
            <Route path="/terms" element={<PageViewer />} />
            <Route path="/returns" element={<PageViewer />} />
            <Route path="/page/:slug" element={<PageViewer />} />
          </Routes>
        </React.Suspense>
      </main>
      
      {!isAdminPath && (
        <footer className="bg-white text-slate-600 pt-16 pb-24 md:pb-12 px-4 md:px-8 border-t border-slate-100">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="space-y-6">
              <Link to="/" className="text-xl font-black tracking-tighter text-slate-900 uppercase flex items-center gap-2">
                {resolvedLogoImage ? (
                  <img src={resolvedLogoImage} alt="Logo" className="h-10 w-[200px] max-w-[200px] object-contain" width={200} height={40} decoding="async" />
                ) : (
                  <>{settings?.logo || ''}</>
                )}
			  </Link>
              {settings?.footer_text && <p className="text-slate-500 text-sm">{settings.footer_text}</p>}
              <div className="flex gap-4">
                {settings?.facebook_link && (
                  <a href={settings.facebook_link} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="p-2 bg-blue-50 rounded-full hover:bg-blue-100 transition-colors">
                    <Facebook className="w-5 h-5 text-blue-600" />
                  </a>
                )}
                {settings?.tiktok_link && (
                  <a href={settings.tiktok_link} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-900">
                      <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/>
                    </svg>
                  </a>
                )}
                {settings?.youtube_link && (
                  <a href={settings.youtube_link} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="p-2 bg-red-50 rounded-full hover:bg-red-100 transition-colors">
                    <Youtube className="w-5 h-5 text-red-600" />
                  </a>
                )}
                {settings?.instagram_link && (
                  <a href={settings.instagram_link} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="p-2 bg-pink-50 rounded-full hover:bg-pink-100 transition-colors">
                    <Instagram className="w-5 h-5 text-pink-600" />
                  </a>
                )}
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-bold text-slate-900 mb-6">Policies</h4>
              <ul className="space-y-4 text-sm text-slate-600">
                <li><Link to="/about" className="hover:text-blue-600 transition-colors">About Us</Link></li>
                <li><Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-blue-600 transition-colors">Terms And Conditions</Link></li>
                <li><Link to="/returns" className="hover:text-blue-600 transition-colors">Return And Cancellation Policy</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold text-slate-900 mb-6">Contact Us</h4>
              <ul className="space-y-4">
                {settings?.contact_email && <li className="flex gap-3 text-sm items-center"><Mail className="w-4 h-4 text-slate-500" /> {settings.contact_email}</li>}
                {settings?.phone && <li className="flex gap-3 text-sm items-center"><Phone className="w-4 h-4 text-slate-500" /> {settings.phone}</li>}
                {settings?.contact_address && <li className="flex gap-3 text-sm items-center"><MapPin className="w-4 h-4 text-slate-500" /> {settings.contact_address}</li>}
              </ul>
            </div>
          </div>
          <div className="max-w-7xl mx-auto mt-16 pt-8 border-t border-slate-100 flex flex-col items-center gap-2 text-slate-500 text-sm">
            {settings?.copyright_text && <p>{settings.copyright_text}</p>}
            <p className="text-xs">
              Developed by{" "}
              <a
                href="https://www.facebook.com/Abu0Hujaifa"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 transition-colors"
              >
                Abu Hujaifa
              </a>
            </p>
          </div>
        </footer>
      )}

      {!isAdminPath && settings?.whatsapp_number && (
        <a 
          href={`https://wa.me/${settings.whatsapp_number.replace(/\+/g, '')}`}
          target="_blank" 
          rel="noopener noreferrer"
          aria-label="Chat on WhatsApp"
          className="fixed bottom-[110px] md:bottom-6 right-4 md:right-6 w-14 h-14 bg-green-500 text-white rounded-full flex items-center justify-center shadow-xl shadow-green-500/40 hover:scale-110 transition-transform z-40"
        >
          <MessageCircle className="w-7 h-7 fill-white" />
        </a>
      )}
      
      {/* Mobile Bottom Nav */}
      {!isAdminPath && (
        <nav className="md:hidden fixed bottom-6 left-4 right-4 bg-white/70 backdrop-blur-2xl border border-white/50 rounded-2xl px-2 py-2 flex justify-between items-center z-50 shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
          <Link to="/" className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all ${location.pathname === '/' ? 'text-slate-900 bg-white shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}>
            <HomeIcon className={`w-5 h-5 ${location.pathname === '/' ? 'stroke-[2.5px]' : ''}`} />
            <span className="text-[10px] font-bold">Home</span>
          </Link>
          <Link to="/shop" className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all ${location.pathname === '/shop' ? 'text-slate-900 bg-white shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}>
            <Search className={`w-5 h-5 ${location.pathname === '/shop' ? 'stroke-[2.5px]' : ''}`} />
            <span className="text-[10px] font-bold">Shop</span>
          </Link>
          <Link to="/track" className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all ${location.pathname === '/track' ? 'text-slate-900 bg-white shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}>
            <Truck className={`w-5 h-5 ${location.pathname === '/track' ? 'stroke-[2.5px]' : ''}`} />
            <span className="text-[10px] font-bold">Track</span>
          </Link>
          <button 
            onClick={toggleCart} 
            aria-label="Open cart"
            className="flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all text-slate-500 hover:text-slate-900 relative"
          >
            <div className="relative">
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-black text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center shadow-sm">
                  {cartCount}
                </span>
              )}
            </div>
            <span className="text-[10px] font-bold">Cart</span>
          </button>
          <Link to={user ? "/profile" : "#"} onClick={!user ? () => setIsAuthOpen(true) : undefined} className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all ${location.pathname === '/profile' ? 'text-slate-900 bg-white shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}>
            <User className={`w-5 h-5 ${location.pathname === '/profile' ? 'stroke-[2.5px]' : ''}`} />
            <span className="text-[10px] font-bold">Profile</span>
          </Link>
          {isAdmin && (
            <Link to="/admin" className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all ${location.pathname.startsWith('/admin') ? 'text-slate-900 bg-white shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}>
              <BarChart3 className={`w-5 h-5 ${location.pathname.startsWith('/admin') ? 'stroke-[2.5px]' : ''}`} />
              <span className="text-[10px] font-bold">Admin</span>
            </Link>
          )}
        </nav>
      )}
    </div>
  );
}
